import { assertNoTokenInUrl } from '../security/redaction';
import { normalizeRouteTemplate, type PublicObservability } from '../observability/metrics';

export type PublicApiClientOptions = {
  baseUrl: string;
  signal?: AbortSignal;
  defaultHeaders?: Record<string, string>;
  observability?: PublicObservability;
};

export type PublicApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  cache?: RequestCache;
};

export class PublicApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly payload?: unknown;

  constructor(message: string, status: number, code?: string, payload?: unknown) {
    super(message);
    this.name = 'PublicApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

export class PublicApiClient {
  private readonly baseUrl: string;
  private readonly signal?: AbortSignal;
  private readonly defaultHeaders: Record<string, string>;
  private readonly observability?: PublicObservability;

  constructor(options: PublicApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.signal = options.signal;
    this.defaultHeaders = options.defaultHeaders || {};
    this.observability = options.observability;
  }

  async request<T>(path: string, options: PublicApiRequestOptions = {}): Promise<T> {
    const requestUrl = this.url(path);
    const startedAt = performance.now();
    const apiRoute = normalizeRouteTemplate(path);
    this.observability?.counter('public_api_request_started_total', { apiRoute });

    try {
      const response = await fetch(requestUrl, {
        method: options.method || 'GET',
        headers: {
          Accept: 'application/json',
          ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...this.defaultHeaders,
          ...(options.headers || {}),
        },
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal || this.signal,
        cache: options.cache,
        credentials: 'include',
      });

      const contentType = response.headers.get('content-type') || '';
      const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text();
      const tags = { apiRoute, apiStatus: response.status, ok: response.ok };
      this.observability?.duration(
        'public_api_request_duration_ms',
        performance.now() - startedAt,
        tags,
      );

      if (!response.ok) {
        this.observability?.counter('public_api_request_failed_total', tags);
        const code = extractApiErrorCode(payload);
        throw new PublicApiError(
          code || `API request failed: ${response.status}`,
          response.status,
          code,
          payload,
        );
      }

      this.observability?.counter('public_api_request_succeeded_total', tags);
      return payload as T;
    } catch (error) {
      if (!(error instanceof PublicApiError)) {
        this.observability?.counter('public_api_request_failed_total', { apiRoute, ok: false });
      }
      throw error;
    }
  }

  private url(path: string): string {
    assertNoTokenInUrl(path, 'public_api_request_url');
    if (/^https?:\/\//i.test(path)) return path;
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${suffix}`;
  }
}

function extractApiErrorCode(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const source = payload as {
    code?: unknown;
    errorCode?: unknown;
    error?: { code?: unknown } | string;
  };
  if (typeof source.error === 'object' && source.error && source.error.code)
    return String(source.error.code);
  if (source.errorCode) return String(source.errorCode);
  if (source.code) return String(source.code);
  if (typeof source.error === 'string') return source.error;
  return undefined;
}
